import {
  createBillingDocumentFromConsumableCharge,
  getBillingDocument,
} from "@/core/billing/billing-documents/billing-documents-store";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import {
  issueConsumableChargeLinesAtomically,
  releaseConsumableChargeLine,
  reserveConsumableChargeLine,
} from "@/core/inventory/consumable-stock-sync";
import {
  getPatientConsumableChargeById,
  patchPatientConsumableCharge,
  patchPatientConsumableChargeLine,
} from "./patient-consumable-store";

export async function reservePatientConsumableChargeStock(
  chargeId: string,
): Promise<{ reserved: string[]; shortage: string[] }> {
  const charge = getPatientConsumableChargeById(chargeId);
  if (!charge) return { reserved: [], shortage: [] };

  const storeId = await pharmacyInventoryApi.getDepotStoreId("consumables");
  if (!storeId) throw new Error("Dépôt WH-CONS introuvable.");

  const balances = await pharmacyInventoryApi.listStockBalancesForStore(storeId);
  const availableByProduct = new Map(
    balances.map((b) => [b.productId, b.quantityAvailable]),
  );

  const reserved: string[] = [];
  const shortage: string[] = [];

  for (const line of charge.lines) {
    const available = availableByProduct.get(line.productId) ?? 0;
    if (available < line.quantity) {
      patchPatientConsumableChargeLine(chargeId, line.id, { stockState: "shortage" });
      shortage.push(line.id);
      continue;
    }
    await reserveConsumableChargeLine(chargeId, line, storeId);
    patchPatientConsumableChargeLine(chargeId, line.id, { stockState: "reserved" });
    reserved.push(line.id);
    availableByProduct.set(line.productId, available - line.quantity);
  }

  return { reserved, shortage };
}

export async function releasePatientConsumableChargeReservations(chargeId: string): Promise<void> {
  const charge = getPatientConsumableChargeById(chargeId);
  if (!charge) return;
  for (const line of charge.lines) {
    if (line.stockState === "reserved") {
      await releaseConsumableChargeLine(chargeId, line);
      patchPatientConsumableChargeLine(chargeId, line.id, { stockState: "none" });
    }
  }
}

/** Sortie stock WH-CONS après encaissement. */
export async function issuePatientConsumableChargeOnPaid(documentId: string): Promise<void> {
  const doc = getBillingDocument(documentId);
  if (!doc?.consumableChargeId || doc.source !== "patient_consumables") return;

  const charge = getPatientConsumableChargeById(doc.consumableChargeId);
  if (!charge || charge.status === "issued") return;

  const storeId = await pharmacyInventoryApi.getDepotStoreId("consumables");
  if (!storeId) throw new Error("Dépôt WH-CONS introuvable.");

  const toIssue = charge.lines.filter((l) => l.stockState === "reserved");
  if (toIssue.length === 0) return;

  await issueConsumableChargeLinesAtomically(
    charge.id,
    toIssue.map((l) => ({ id: l.id, productId: l.productId, quantity: l.quantity })),
    storeId,
  );

  const lines = charge.lines.map((line) =>
    line.stockState === "reserved" ? { ...line, stockState: "issued" as const } : line,
  );
  patchPatientConsumableCharge(charge.id, { status: "issued", lines });
}

export function handleBillingDocumentPaidForConsumableCharge(documentId: string): void {
  void issuePatientConsumableChargeOnPaid(documentId).catch((err) => {
    console.warn("[Hospitol] Sortie stock consommables patient échouée", err);
  });
}

export async function submitPatientConsumableChargeToBilling(chargeId: string): Promise<{
  ok: boolean;
  documentId?: string;
  shortage?: string[];
  error?: string;
}> {
  const charge = getPatientConsumableChargeById(chargeId);
  if (!charge) return { ok: false, error: "Fiche introuvable." };
  if (charge.status !== "draft") return { ok: false, error: "Fiche déjà facturée." };

  try {
    const { reserved, shortage } = await reservePatientConsumableChargeStock(chargeId);
    if (reserved.length === 0) {
      return {
        ok: false,
        shortage,
        error: shortage.length > 0 ? "Stock insuffisant sur WH-CONS." : "Aucune ligne à facturer.",
      };
    }

    const doc = await createBillingDocumentFromConsumableCharge(chargeId);
    if (!doc) {
      await releasePatientConsumableChargeReservations(chargeId);
      return { ok: false, error: "Création facture impossible." };
    }

    patchPatientConsumableCharge(chargeId, {
      status: "billed",
      billingDocumentId: doc.id,
    });

    return { ok: true, documentId: doc.id, shortage };
  } catch (err) {
    await releasePatientConsumableChargeReservations(chargeId);
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Erreur facturation consommables.",
    };
  }
}
